import { memo, useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { FORMATTERS } from "app/common/benchmark/formatters";
import useRankingData from "app/pages/benchmark-tracker/hooks/useRankingData";
import type { BenchmarkMetricName, RankingDataSchema } from "app/pages/benchmark-tracker/types/api.types";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import Chart from "pro-features/components/Chart/src/Chart";

import { MetricFormat } from "app/common/benchmark/types";

import { getBarChartConfig } from "./config";
import Legend from "./Legend/Legend";
import { ChartContainer } from "./styles";

type WidgetChartSchema = {
    format: MetricFormat;
    data: RankingDataSchema;
    isInverted: boolean;
    name: BenchmarkMetricName;
    onOpenModal: () => void;
    isRelative?: boolean;
};

const WidgetChart = ({ data, format, isInverted, name, onOpenModal, isRelative }: WidgetChartSchema) => {
    const {
        params: { granularity },
    } = useCurrentStateAndParams();

    const [isDifference, setIsDifference] = useState(false);

    const percentFormatter = FORMATTERS[MetricFormat.percentSign];

    const formatter = isDifference ? percentFormatter : FORMATTERS[format];
    const { chartData, diffChartData } = useRankingData(data, isDifference);

    const selectedChartData = (isDifference ? diffChartData : chartData?.structuredChartData) ?? [];

    const chartConfig = useMemo(
        () =>
            getBarChartConfig(
                granularity,
                selectedChartData,
                isDifference,
                formatter,
                isInverted,
                name,
                chartData?.dates ?? [],
                isRelative,
            ),
        [granularity, selectedChartData, isDifference, formatter, isInverted, name, chartData?.dates, isRelative],
    );

    const onDifferenceClick = () => {
        setIsDifference((state) => !state);
    };

    return (
        <ChartContainer>
            <Legend
                chartData={chartData?.structuredChartData ?? []}
                onClick={onDifferenceClick}
                isDifferenceSelected={isDifference}
                onOpenModal={onOpenModal}
            />
            <Chart
                type={chartTypes.COLUMN}
                data={selectedChartData}
                config={chartConfig}
            />
        </ChartContainer>
    );
};

export default memo(WidgetChart);
